import { apiClient } from './client';
import type {
  Conversation,
  ConversationListResponse,
  Message,
  Label,
  SendMessageBody,
} from '@/types/chat.types';

export interface ListConversationsParams {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

interface RawConversation {
  id: string;
  clientId: string;
  assignedOperator: string | null;
  status: string;
  flowState?: 'bot' | 'soporte';
  labelIds: string[];
  unreadOperator: number;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  createdAt: string;
  client: { id: string; nombre: string; apellido: string | null; casinoUsername: string; isVip: boolean } | null;
  operator: { id: string; displayName: string | null; username: string } | null;
}

export const chatApi = {
  listConversations: async (params: ListConversationsParams = {}): Promise<ConversationListResponse> => {
    // El backend devuelve camelCase; lo mapeamos al snake_case que esperan los componentes.
    const { data } = await apiClient.get<RawConversation[]>('/chat/conversations', { params });
    const mapped = data.map((c): Conversation => ({
      id: c.id,
      client_id: c.clientId,
      client: c.client
        ? {
            id: c.client.id,
            nombre: c.client.nombre,
            apellido: c.client.apellido ?? null,
            casino_username: c.client.casinoUsername,
            estatus: '',
            saldo: 0,
            wager: 0,
            saldo_cobrable: 0,
          }
        : null,
      status: c.status,
      flowState: c.flowState,
      last_message: c.lastMessagePreview ?? null,
      last_message_at: c.lastMessageAt ?? null,
      unread_count: c.unreadOperator ?? 0,
      assigned_to_id: c.assignedOperator ?? null,
      assigned_to_name: c.operator?.displayName ?? c.operator?.username ?? null,
      labels: c.labelIds ?? [],
      created_at: c.createdAt,
      updated_at: c.createdAt,
    }));
    return {
      data: mapped,
      total: mapped.length,
      limit: params.limit ?? mapped.length,
      offset: params.offset ?? 0,
    };
  },

  getMessages: async (conversationId: string): Promise<Message[]> => {
    // El endpoint real es GET /chat/messages?conversationId=... (backend ordena desc; invertimos a asc)
    const { data } = await apiClient.get<Message[]>('/chat/messages', { params: { conversationId } });
    return [...data].reverse();
  },

  sendMessage: async (body: SendMessageBody): Promise<Message> => {
    const { data } = await apiClient.post<Message>('/chat/messages', body);
    return data;
  },

  getLabels: async (): Promise<Label[]> => {
    const { data } = await apiClient.get<Label[]>('/chat/labels');
    return data;
  },

  updateConversation: async (
    id: string,
    body: { flowState?: 'bot' | 'soporte'; status?: string; assignedOperator?: string | null },
  ): Promise<Conversation> => {
    const { data } = await apiClient.patch<Conversation>(`/chat/conversations/${id}`, body);
    return data;
  },
};
